import { get } from 'lodash';
import moment from 'moment';
import { actionHandlerWithFeatureFlag, retrieveObjectMapper } from 'utils';
import { DEFAULT_ERROR, FEATURE_FLAGS } from 'utils/constants';
import DatadogHandler from 'utils/datadog';
import SkRequest from 'redux/actions/BaseRequest';
import {
  onboardingService,
  MS_PHASE_3_ENDPOINTS,
  schoolService,
  uacService,
} from '../utils/msEndpointConstants';
import { SAVE_DATE_FORMAT } from '../utils/constants';

const fileName = 'src/services/ConfigService.js';
const { REGISTRATIONS, SCHOOL, UAC } = MS_PHASE_3_ENDPOINTS;

export const msGetSchoolConfig = async (_, { filter }) => {
  try {
    const schoolSvc = schoolService();
    const resp = await schoolSvc
      .get(SCHOOL.getAllSchoolConfigs(get(filter, 'fkSchool')))
      .then(res => ({
        totalCount: get(res, 'data.totalCount', 0),
        data: get(res, 'data.data', []),
      }))
      .catch(() => ({
        totalCount: 0,
        data: [],
      }));

    return {
      data: {
        findAllSchoolConfig: {
          data: get(resp, 'data', [])?.map(d =>
            retrieveObjectMapper(d, ['ID:id', 'fk_school', 'value', 'key'])
          ),
          totalCount: get(resp, 'totalCount', 0),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetSchoolConfig',
        fileName,
      },
      'error'
    );
    return {
      data: {
        findAllSchoolConfig: {
          data: [],
          totalCount: 0,
        },
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const fetchThemeConfig = async (filter = {}) => {
  const fkSchool = get(filter, 'fkSchool');
  return actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msGetSchoolConfig,
      monoHandler: () => {
        return SkRequest({
          data: {
            query: `query { findAllSchoolConfig(filter: {fkSchool: ${fkSchool}}) { totalCount data { ID fkSchool value key } } }`,
          },
          method: 'POST',
        });
      },
      extraData: { filter },
    }
  );
};

export const msFetchSidemenuConfig = async (_, reqBody) => {
  try {
    const fkSchool = get(reqBody, 'fkSchool');
    const uacSvc = uacService();

    const resp = await uacSvc
      .post(...UAC.getSidemenuConfig(fkSchool))
      .then(res => ({
        totalCount: get(res, 'data.totalCount', 0),
        data: get(res, 'data.data', []),
      }))
      .catch(() => ({
        totalCount: 0,
        data: [],
      }));

    return {
      data: {
        findSidebarMenuByUser: {
          data: get(resp, 'data', [])?.map(d =>
            retrieveObjectMapper(d, [
              'ID:id',
              'icon',
              'path',
              'active',
              'label',
              'fk_navigation',
            ])
          ),
          totalCount: get(resp, 'totalCount', 0),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchSidemenuConfig',
        fileName,
      },
      'error'
    );
    return {
      data: {
        findSidebarMenuByUser: {
          data: [],
          totalCount: 0,
        },
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSuggestLevels = async (_, reqData) => {
  try {
    const reqBody = {
      ...(reqData?.IDCentre && {
        centre_id: reqData?.IDCentre,
      }),
      ...(reqData?.dateOfBirth && {
        date_of_birth: reqData?.dateOfBirth,
      }),
      ...(reqData?.enrollmentDate && {
        enrolment_date: moment(reqData?.enrollmentDate).format(
          SAVE_DATE_FORMAT
        ),
      }),
      ...(reqData?.IDSchool && {
        school_id: reqData?.IDSchool,
      }),
    };
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...REGISTRATIONS.suggestLevels(reqBody)
    );
    const data = resp?.data?.levels || [];

    return {
      success: true,
      data: {
        suggestLevels: data?.map(dt => ({
          ...retrieveObjectMapper(dt, [
            'ID:id',
            'code',
            'label',
            'from_month',
            'to_month',
          ]),
        })),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSuggestLevels',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      errors: [get(error, 'message', DEFAULT_ERROR)],
    };
  }
};

export const msSuggestLevel = async (_, reqData) => {
  try {
    const reqBody = {
      ...(reqData?.dateOfBirth && {
        date_of_birth: reqData?.dateOfBirth,
      }),
      ...(reqData?.enrollmentDate && {
        enrolment_date: moment(reqData?.enrollmentDate).format(
          SAVE_DATE_FORMAT
        ),
      }),
      ...(reqData?.IDSchool && {
        school_id: reqData?.IDSchool,
      }),
    };
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...REGISTRATIONS.suggestLevel(reqBody)
    );
    const level = resp?.data || {};

    return {
      success: true,
      data: {
        suggestLevel: retrieveObjectMapper(level, [
          'ID:id',
          'code',
          'label',
          'from_month',
          'to_month',
          'fk_school',
        ]),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSuggestLevel',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      errors: [get(error, 'message', DEFAULT_ERROR)],
    };
  }
};

export const msSuggestPrograms = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: { suggestProgram: { data: [], totalCount: 0 } },
          success: true,
        });
      }, 1000);
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSuggestPrograms',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};
